import { makeRequest } from "./request";

export type GetOrganizationFileOptions = {
  organizationIdOverride?: string;
};

export type GetOrganizationFileResult = {
  objectUrl: string;
  revokeObjectUrl: () => void;
};

/**
 * True if the URL points at the API's organization-files endpoint, in either
 * the current `/v1/organization-files/<rest>` form or the retired
 * `/v1/organizations/<orgId>/files/<rest>` form. These URLs cannot be fetched
 * bare — they need the auth + Organization-Id headers, so callers must resolve
 * them through getOrganizationFile instead.
 */
export const isOrganizationFileUrl = (url: string): boolean => {
  return url.includes("/v1/organization-files/") || url.includes("/v1/organizations/");
};

export const getOrganizationFile = async (organizationFileUrl: string): Promise<GetOrganizationFileResult> => {
  if (!organizationFileUrl) {
    throw new Error("File URL is required");
  }

  // Get signed url from API
  let signedUrl: string;
  try {
    const { pathname } = new URL(organizationFileUrl);
    // Older uploads stored the retired `/v1/organizations/<orgId>/files/<rest>`
    // path; the file endpoint is now the flat `/v1/organization-files/<rest>`.
    // The GCS object is identical, so rewrite the path before requesting it.
    const normalizedPath = pathname.replace(/^\/v1\/organizations\/[^/]+\/files\//, "/v1/organization-files/");
    const response = await makeRequest<{ signedUrl: string }>(`${normalizedPath}?format=url`);
    if (!response.signedUrl) {
      throw new Error("Missing signed URL in response");
    }
    signedUrl = response.signedUrl;
  } catch (error) {
    throw new Error(`Failed to resolve organization file URL: ${(error as Error).message}`);
  }

  // Get file blob via signed url
  const blobResponse = await fetch(signedUrl);
  if (!blobResponse.ok) {
    throw new Error(`Failed to download organization file: ${blobResponse.status}`);
  }
  const blob = await blobResponse.blob();
  const objectUrl = URL.createObjectURL(blob);

  return {
    objectUrl,
    revokeObjectUrl: () => URL.revokeObjectURL(objectUrl),
  };
};
